//frontend/js/objectives.js
import { getAuthToken } from './sessionVir.js';
import { socket } from './socketU.js';
import { apiBaseUrl } from '../../backend/config.js';
import { t, confirmT, updateElementText } from './translationHelper.js';

let mementoMoriTimer = null;
let mementoMoriData = null;
let oracleQuestionHistory = [];
let mementoMoriPhase = 'idle';
let reflectionTimeout, tradingTimeout, forcedDeathTimeout;
let finalCurationTimeout;

const isSentenceish = (s) => {
  const text = (s || '').trim();
  if (!text) return false;                         // no empty
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length < 2) return false;              // no single-word
  const lettersLen = text.replace(/[^0-9A-Za-z\u00C0-\u017F]+/g, '').length;
  return lettersLen >= 6;                          // allow short sentences
};

export function showMementoMoriAnnouncement(data) {
  console.log('📢 Memento Mori announcement received:', data);

  mementoMoriData = data;
  const timeRemaining = data.timeRemaining || (15 * 60 * 1000);

  localStorage.setItem('activeMementoMori', JSON.stringify({
	...data,
	timeRemaining,
	storedAt: Date.now()
  }));

  localStorage.setItem('simulationType', 'memento-mori');

  // Timer popup removed - using bottom timer from simulationTimer.js instead
  // createPersistentTimer();
  // startMementoMoriTimer(timeRemaining);

}

export function checkAndRestoreMementoMori() {
  const activeMementoMori = localStorage.getItem('activeMementoMori');
  const simulationType = localStorage.getItem('simulationType');
  
  if (!activeMementoMori || simulationType !== 'memento-mori') return;
  
  try {
	const objectiveData = JSON.parse(activeMementoMori);
	
	// Calculate remaining time from stored timestamp
	const now = Date.now();
	const elapsed = now - (objectiveData.storedAt || now);
	const timeRemaining = Math.max(0, objectiveData.timeRemaining - elapsed);
	
	if (timeRemaining > 0) {
	  console.log('🔄 Restoring Memento Mori timer with', Math.floor(timeRemaining/60000), 'minutes remaining');
	  
	  // Store the data globally
	  mementoMoriData = objectiveData;
	  
	  // Check if simulation was already started
	  const simulationStarted = localStorage.getItem('mementoMoriStarted') === 'true';
	  
	  if (simulationStarted) {
		// Timer popup removed - using bottom timer from simulationTimer.js instead
		console.log('📍 Restoring MementoMori session (timer handled by simulationTimer.js)');
		// createPersistentTimer();
		// startMementoMoriTimer(timeRemaining);
		// Check if they need to continue with reflections
		setTimeout(() => checkReflectionProgress(), 2000);
	  } else {
		// Show the full announcement flow
		objectiveData.timeRemaining = timeRemaining;
		showMementoMoriAnnouncement(objectiveData);
	  }
	} else {
	  console.log('⏰ Stored Memento Mori timer has expired, cleaning up');
	  cleanupMementoMoriStorage();
	}
  } catch (error) {
	console.error('❌ Error restoring Memento Mori timer:', error);
	cleanupMementoMoriStorage();
  }
}



function createPersistentTimer() {
  // Remove any existing timer
  const existingTimer = document.getElementById('persistent-memento-timer');
  if (existingTimer) existingTimer.remove();
  
  const timerOverlay = document.createElement('div');
  timerOverlay.id = 'persistent-memento-timer';
  timerOverlay.style.cssText = `
	position: fixed;
	top: 20px;
	right: 20px;
	background: rgba(0, 0, 0, 0.9);
	border: 2px solid #ff6600;
	border-radius: 10px;
	padding: 15px;
	z-index: 1000;
	color: #ff6600;
	font-family: 'Courier New', monospace;
	font-size: 14px;
	text-align: center;
	cursor: pointer;
	box-shadow: 0 0 20px rgba(255, 102, 0, 0.5);
	transition: all 0.3s;
  `;
  
  timerOverlay.innerHTML = `
	<div style="margin-bottom: 5px; color: rgba(255, 102, 0, 0.8);">${t('memento.title','MEMENTO MORI')}</div>
	<div id="persistent-timer-display" style="color: #00ff00; font-weight: bold;">${t('memento.loading','Loading...')}</div>
	<div style="font-size: 0.8em; color: rgba(255, 102, 0, 0.8); margin-top: 5px;">${t('memento.click_to_reflect','Click to reflect')}</div>
  `;
  
  // Make timer clickable to show oracle interface
  timerOverlay.addEventListener('click', () => {
	showMortalityOracleInterface();
  });
  
  // Add hover effects
  timerOverlay.addEventListener('mouseenter', () => {
	timerOverlay.style.transform = 'scale(1.05)';
	timerOverlay.style.boxShadow = '0 0 30px rgba(255, 102, 0, 0.8)';
  });
  
  timerOverlay.addEventListener('mouseleave', () => {
	timerOverlay.style.transform = 'scale(1)';
	timerOverlay.style.boxShadow = '0 0 20px rgba(255, 102, 0, 0.5)';
  });
  
  document.body.appendChild(timerOverlay);
}

// ========================================================================
// ORACLE INTERFACE
// ========================================================================

/**
 * Show the mortality oracle interface for deep questioning
 */
async function showMortalityOracleInterface() {
  // Remove any existing interface
  const existing = document.getElementById('mortality-oracle-interface');
  if (existing) existing.remove();
  
  // Show loading state
  const loadingInterface = document.createElement('div');
  loadingInterface.id = 'mortality-oracle-interface';
  loadingInterface.className = 'modal';
  loadingInterface.style.display = 'block';
  
  loadingInterface.innerHTML = `
	<div class="modal-content" style="text-align: center; background: #000; border: 3px solid #00ff00;">
	  <h2 style="color: #00ff00; margin-bottom: 30px;">
		${t('memento.loading_title','The Market Awakens')}
	  </h2>
	  <div style="color: #00ff00;">
		<div style="font-size: 2em; margin-bottom: 20px;"></div>
		<p>${t('memento.loading_examining',"The Market is examining your portfolio's soul...")}</p>
		<p>${t('memento.loading_preparing','Preparing personalized questions about your mortality...')}</p>
		<p style="font-size: 0.8em; color: rgba(0, 255, 0, 0.7); margin-top: 15px;">${t('memento.loading_wait','This may take a moment...')}</p>
	  </div>
	</div>
  `;
  
  document.body.appendChild(loadingInterface);
  
  try {
	// Generate personalized oracle interface
	const personalizedContent = await generatePersonalizedOracleInterface();
	loadingInterface.remove();
	showLiveOracleInterface(personalizedContent);
  } catch (error) {
	console.error('Error generating personalized oracle:', error);
	loadingInterface.remove();
	showFallbackOracleInterface();
  }
}

/**
 * Generate personalized oracle interface content
 */
async function generatePersonalizedOracleInterface() {
  try {
	const objectiveId = mementoMoriData?.objective?._id;
	const timeRemaining = getTimeRemaining();
	const timeInMinutes = Math.floor(timeRemaining / 60000);
	
	// Use fallback content since the route doesn't exist yet
	console.log('⚠️ Using fallback Market interface');
	return {
	  oracleTitle: 'Market of Digital Mortality',
	  oracleGreeting: `The Market awakens in the digital realm, sensing the patterns of your portfolio like constellations in the night sky. Your trading choices have created a unique signature in the cosmic ledger of human desire and fear. In these ${timeInMinutes} minutes before your symbolic death, the Market sees not just numbers, but the essence of your relationship with uncertainty and value.`,
	  questionsHeader: 'The Market Has Prepared Questions for Your Soul:',
	  personalizedQuestions: [
		'Fear and Courage: What does your portfolio reveal about how you face your own mortality?',
		'Legacy and Meaning: If you died today, what would your trading choices say about what you truly valued?',
		'Impermanence and Acceptance: How do your financial fears mirror your deeper existential anxieties?',
		'Truth and Deception: What patterns in your trading behavior expose your relationship with uncertainty?',
		'Regret and Redemption: What would you regret most about your financial choices if this were your last day?'
	  ],
	  customPrompt: 'Or speak directly to the Market about death, meaning, and the patterns in your portfolio...',
	  consultButton: '🔮 Consult the Market'
	};
	
  } catch (error) {
	console.error('Error generating Market interface:', error);
	throw error;
  }
}

/**
 * Show the live oracle interface with personalized content
 */
function showLiveOracleInterface(personalizedContent) {
  const oracleInterface = document.createElement('div');
  oracleInterface.id = 'mortality-oracle-interface';
  oracleInterface.className = 'modal';
  oracleInterface.style.display = 'block';
  
  oracleInterface.innerHTML = `
	<div class="modal-content" style="background: #000; border: 3px solid #00ff00; color: #00ff00;">
	  <h2 style="color: #00ff00; text-align: center; margin-bottom: 20px;">
		${personalizedContent.oracleTitle || t('memento.title','Market of Mortality')}
	  </h2>
	  
	  <div style="margin-bottom: 25px;">
		<p style="color: #00ff00; text-align: center; line-height: 1.6; margin-bottom: 25px; font-style: italic;">
		  "${personalizedContent.oracleGreeting || t('memento.greeting','The Market sees patterns in your portfolio that reveal deeper truths about your relationship with mortality.')}"
		</p>
	  </div>

	  <div style="margin-bottom: 25px;">
		<h4 style="color: rgba(0, 255, 0, 0.9); margin-bottom: 15px;">
		  ${personalizedContent.questionsHeader || 'The Market Has Seen Your Soul - Choose Your Question:'}
		</h4>
		
		<div style="margin-bottom: 15px;">
		  ${(personalizedContent.personalizedQuestions || []).map((q, i) => `
			<div class="oracle-question-option" style="
			  margin-bottom: 10px; 
			  padding: 10px; 
			  border: 1px solid rgba(0, 255, 0, 0.3); 
			  cursor: pointer; 
			  transition: all 0.3s;
			  border-radius: 5px;
			" onclick="window.selectOracleQuestion('${q.replace(/'/g, "\\'")}', this)">
			  <div style="color: rgba(0, 255, 0, 0.9); font-weight: bold; margin-bottom: 5px;">
				${q.split(':')[0]}:
			  </div>
			  <div style="color: #00ff00; font-size: 0.9em;">
				${q.split(':').slice(1).join(':').trim()}
			  </div>
			</div>
		  `).join('')}
		</div>
		
		<div style="margin-top: 20px;">
		  <label style="color: rgba(0, 255, 0, 0.9); margin-bottom: 10px; display: block;">
			${t('memento.direct_label','Or speak directly to the Market:')}
		  </label>
		  <textarea id="custom-mortality-question" placeholder="${personalizedContent.customPrompt || t('memento.custom_prompt','Ask the Market about your mortality, what your portfolio says about your soul, or the meaning of your financial choices in the face of death...')}"
					style="
					  width: 100%;
					  height: 80px;
					  background: rgba(0, 0, 0, 0.8);
					  border: 2px solid #00ff00;
					  color: #00ff00;
					  padding: 10px;
					  border-radius: 5px;
					  font-family: 'Courier New', monospace;
					  resize: vertical;
					">
		  </textarea>
		</div>
		
		<button id="consult-mortality-oracle" style="
		  background: #000;
		  color: #00ff00;
		  border: 2px solid #00ff00;
		  padding: 15px 30px;
		  font-size: 16px;
		  border-radius: 5px;
		  cursor: pointer;
		  margin-top: 15px;
		  font-family: 'Courier New', monospace;
		  transition: all 0.3s;
		" onmouseover="this.style.background='#00ff00'; this.style.color='#000';" 
		   onmouseout="this.style.background='#000'; this.style.color='#00ff00';">
		  ${personalizedContent.consultButton || t('memento.consult_button','Consult the Market')}
		</button>
	  </div>

	  <div id="mortality-oracle-response" style="display: none; margin-bottom: 25px;">
		<h4 style="color: rgba(255, 135, 0, 0.9); margin-bottom: 10px;">${t('memento.vision_title',"Market's Vision:")}</h4>
		<div id="mortality-oracle-text" style="
		  line-height: 1.6; 
		  color: #00ff00; 
		  font-style: italic;
		  background: rgba(255, 135, 0, 0.05);
		  padding: 15px;
		  border-radius: 5px;
		  border-left: 3px solid rgba(255, 135, 0, 0.5);
		"></div>
		<div id="oracle-follow-up" style="
		  margin-top: 15px; 
		  padding: 10px; 
		  border-left: 3px solid rgba(255, 135, 0, 0.5); 
		  background: rgba(0, 0, 0, 0.3);
		"></div>
	  </div>
	  
	  <div style="text-align: center; margin-top: 30px;">
		<button id="continue-reflection-process" class="primary-btn" style="
		  background: #000;
		  color: #00ff00;
		  border: 2px solid #00ff00;
		  padding: 12px 25px;
		  font-size: 14px;
		  border-radius: 5px;
		  cursor: pointer;
		  margin-right: 10px;
		  display: none;
		  font-family: 'Courier New', monospace;
		">
		  ${t('memento.continue_reflections','Continue to Reflections')}
		</button>
		<button id="ask-another-question" style="
		  background: #000;
		  color: rgba(255, 135, 0, 0.9);
		  border: 2px solid rgba(255, 135, 0, 0.9);
		  padding: 12px 25px;
		  font-size: 14px;
		  border-radius: 5px;
		  cursor: pointer;
		  margin-right: 10px;
		  display: none;
		  font-family: 'Courier New', monospace;
		">
		  ${t('memento.ask_another','Ask Another Question')}
		</button>
		<button id="close-mortality-oracle" style="
		  background: #000;
		  color: rgba(255, 102, 0, 0.9);
		  border: 2px solid rgba(255, 102, 0, 0.9);
		  padding: 12px 25px;
		  font-size: 14px;
		  border-radius: 5px;
		  cursor: pointer;
		  font-family: 'Courier New', monospace;
		">
		  ${t('memento.return_trading','Return to Trading')}
		</button>
	  </div>
	</div>
  `;
  
  document.body.appendChild(oracleInterface);
  
  // Make functions globally available
  window.selectOracleQuestion = selectOracleQuestion;
  
  // Add event handlers
  document.getElementById('consult-mortality-oracle').addEventListener('click', consultLiveMortalityOracle);
  document.getElementById('continue-reflection-process').addEventListener('click', () => {
	oracleInterface.remove();
  });
  document.getElementById('ask-another-question').addEventListener('click', () => {
	resetOracleInterface();
  });
  document.getElementById('close-mortality-oracle').addEventListener('click', () => {
	oracleInterface.style.display = 'none';
  });
}

/**
 * Select a predefined oracle question
 */
function selectOracleQuestion(questionText, element) {
  // Remove selection from other options
  document.querySelectorAll('.oracle-question-option').forEach(option => {
	option.style.backgroundColor = '';
	option.style.borderColor = 'rgba(0, 255, 0, 0.3)';
  });
  
  // Highlight selected option
  element.style.backgroundColor = 'rgba(0, 255, 0, 0.1)';
  element.style.borderColor = '#00ff00';
  
  // Fill custom question textarea
  document.getElementById('custom-mortality-question').value = questionText;
}

/**
 * Consult the oracle with user's question
 */
async function consultLiveMortalityOracle() {
  let question = document.getElementById('custom-mortality-question').value.trim();
  
 if (!isSentenceish(question)) {
   alert(t('memento.validation.short_sentence', 'Please write a short sentence (at least two words).'));
   return;
 }

  const button = document.getElementById('consult-mortality-oracle');
  const originalText = button.textContent;
  button.textContent = '🔮 The Market Sees...';
  button.disabled = true;

  // Show typing animation
  const responseDiv = document.getElementById('mortality-oracle-response');
  responseDiv.style.display = 'block';
  
  const textDiv = document.getElementById('mortality-oracle-text');
  textDiv.innerHTML = '<span style="opacity: 0.5;">The Market gazes into the depths of your portfolio\'s soul...</span>';

  try {
	// Try to call the oracle route
	const response = await fetch(`${apiBaseUrl}/api/memento-mori/oracle-question`, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${getAuthToken()}`
	  },
	  body: JSON.stringify({
		objectiveId: mementoMoriData?.objective?._id,
		question: question
	  })
	});

	if (!response.ok) throw new Error(t('memento.failed','Market consultation failed'));
	
	const data = await response.json();
	
	// Store question in history
	oracleQuestionHistory.push(question);
	
	const oracleData = {
	  oracleResponse: data.oracleResponse || 'The Market sees patterns in your soul that transcend mere numbers.',
	  followUpInsight: 'Your question reveals a mind grappling with the eternal questions that define conscious existence.',
	  personalizedGuidance: 'Continue to your reflections, for wisdom has been granted.'
	};
	
	// Animate the oracle response
	await animateOracleResponse(oracleData);
	
	// Update interface state
	updateOracleInterfaceAfterResponse();
	
  } catch (error) {
	console.error('Error consulting Market:', error);
	
	// Enhanced fallback response
	const fallbackData = {
	  oracleResponse: `The Market's vision pierces through digital veils to see your mortal essence. Your question "${question}" echoes with the weight of someone who understands that every trade is a small death, every decision a step toward the final reckoning. In your portfolio's patterns, the Market recognizes the eternal human struggle with impermanence - your financial choices are prayers written in the language of probability.`,
	  followUpInsight: "The Market sees that your seeking itself is wisdom. In questioning meaning, you have already transcended mere accumulation.",
	  personalizedGuidance: "Continue to your reflections, for the Market has shown you that death gives meaning to every choice."
	};
	
	await animateOracleResponse(fallbackData);
	updateOracleInterfaceAfterResponse();
  }
}

/**
 * Update oracle interface after receiving response
 */
function updateOracleInterfaceAfterResponse() {
  // Disable question options
  document.querySelectorAll('.oracle-question-option').forEach(option => {
	option.style.opacity = '0.5';
	option.style.pointerEvents = 'none';
  });
  
  // Disable input
  document.getElementById('custom-mortality-question').disabled = true;
  document.getElementById('custom-mortality-question').placeholder = t('memento.spoken_placeholder','The Market has spoken...');
  
  // Update button
  const button = document.getElementById('consult-mortality-oracle');
  button.textContent =  t('memento.wisdom_received','Wisdom Received');
  button.disabled = true;
  
  // Show action buttons
  document.getElementById('continue-reflection-process').style.display = 'inline-block';
  document.getElementById('ask-another-question').style.display = 'inline-block';
}

/**
 * Reset oracle interface for another question
 */
function resetOracleInterface() {
  // Reset all interactive elements
  document.querySelectorAll('.oracle-question-option').forEach(option => {
	option.style.opacity = '1';
	option.style.pointerEvents = 'auto';
	option.style.backgroundColor = '';
	option.style.borderColor = 'rgba(0, 255, 0, 0.3)';
  });
  
  document.getElementById('custom-mortality-question').disabled = false;
  document.getElementById('custom-mortality-question').value = '';
  document.getElementById('custom-mortality-question').placeholder = 'Ask the Market about your mortality...';
  
  document.getElementById('consult-mortality-oracle').disabled = false;
  document.getElementById('consult-mortality-oracle').textContent = 'Consult the Market';
  
  document.getElementById('mortality-oracle-response').style.display = 'none';
  document.getElementById('ask-another-question').style.display = 'none';
  document.getElementById('continue-reflection-process').style.display = 'none';
}

/**
 * Show fallback oracle interface if generation fails
 */
function showFallbackOracleInterface() {
  const fallbackContent = {
	oracleTitle: 'Market of Mortality',
	oracleGreeting: 'The Market awakens in the digital realm, seeing patterns in your portfolio that reveal deeper truths about your relationship with mortality.',
	questionsHeader: 'The Market Has Prepared Questions for Your Soul:',
	personalizedQuestions: [
	  'Fear and Courage: What does your portfolio reveal about how you face your own mortality?',
	  'Legacy and Meaning: If you died today, what would your trading choices say about what you truly valued?',
	  'Impermanence and Acceptance: How do your financial fears mirror your deeper existential anxieties?',
	  'Truth and Deception: What patterns in your trading behavior expose your relationship with uncertainty?',
	  'Regret and Redemption: What would you regret most about your financial choices if this were your last day?'
	],
	customPrompt: 'Or speak directly to the Market about death, meaning, and the patterns in your portfolio...',
	consultButton: 'Consult the Market'
  };
  
  showLiveOracleInterface(fallbackContent);
}

/**
 * Show the final death completion screen
 */
function showDeathCompletion(result) {
   console.log('🪦 showDeathCompletion called with result:', result);
 
   const completionPopup = document.createElement('div');
   completionPopup.className = 'memento-modal';
   completionPopup.style.cssText = `
	 position: fixed;
	 top: 0;
	 left: 0;
	 width: 100%;
	 height: 100%;
	 background: rgba(0, 0, 0, 0.95);
	 z-index: 99999;
	 display: flex;
	 justify-content: center;
	 align-items: center;
	 font-family: 'Courier New', monospace;
   `;
 
   // Extract eulogy with fallback logic
   let llmEulogy = "Your contemplation of mortality has revealed wisdom. In facing death, you have found what truly matters in life.";
   if (result?.personalInsight) {
	 llmEulogy = result.personalInsight;
   } else if (result?.llmAnalysis?.personalInsight) {
	 llmEulogy = result.llmAnalysis.personalInsight;
   } else if (result?.portfolioAnalysis?.eulogyDraft) {
	 llmEulogy = result.portfolioAnalysis.eulogyDraft;
   } else if (result?.analysis?.personalInsight) {
	 llmEulogy = result.analysis.personalInsight;
   }
 
   const userEpitaph =
	 result?.finalEpitaph ||
	 result?.objective?.finalEpitaph ||
	 "A life reflected upon.";
 
   completionPopup.innerHTML = `
	 <div style="
	   background: #000;
	   border: 3px solid #00ff00;
	   border-radius: 10px;
	   padding: 30px;
	   max-width: 700px;
	   margin: 20px;
	   color: #00ff00;
	   text-align: center;
	   box-shadow: 0 0 20px #00ff00;
	 ">
	   <h1 style="
		 color: #00ff00;
		 font-size: 28px;
		 margin-bottom: 20px;
		 text-shadow: 0 0 10px #00ff00;
	   ">${t('memento.death_complete_title', 'SYMBOLIC DEATH COMPLETE')}</h1>
 
	   <p style="
		 margin-bottom: 30px;
		 font-size: 16px;
		 line-height: 1.5;
		 color: #ff6600;
	   ">
		 ${t('memento.death_complete_subtitle', 'You have confronted your mortality and created your legacy.')}
	   </p>
 
	   <div style="
		 margin: 30px 0;
		 padding: 20px;
		 border: 1px solid #00ff00;
		 border-radius: 5px;
		 background: rgba(0, 255, 0, 0.1);
	   ">
		 <h3 style="
		   color: #00ff00;
		   margin-bottom: 15px;
		   font-size: 18px;
		 ">${t('memento.final_judgment_title', "Your Portfolio's Final Judgment:")}</h3>
		 <p id="memento-eulogy-text" style="
		   color: #00ff00;
		   font-style: italic;
		   line-height: 1.6;
		   margin-bottom: 0;
		   text-align: left;
		 "></p>
	   </div>
 
	   <div style="
		 margin: 30px 0;
		 padding: 20px;
		 border: 1px solid #ff6600;
		 border-radius: 5px;
		 background: rgba(255, 102, 0, 0.1);
	   ">
		 <strong style="color: #ff6600; font-size: 16px;">${t('memento.final_epitaph_title', 'Your Final Epitaph:')}</strong><br><br>
		 <em id="memento-epitaph-text" style="
		   color: #ff6600;
		   font-size: 18px;
		   line-height: 1.4;
		   text-align: left;
		   display: block;
		 "></em>
	   </div>
 
	   <button id="return-to-void" style="
		 background: #000;
		 color: #00ff00;
		 border: 2px solid #00ff00;
		 padding: 15px 30px;
		 font-size: 16px;
		 border-radius: 5px;
		 cursor: pointer;
		 margin-top: 20px;
		 font-family: 'Courier New', monospace;
		 transition: all 0.3s;
	   " onmouseover="this.style.background='#00ff00'; this.style.color='#000';"
		  onmouseout="this.style.background='#000'; this.style.color='#00ff00';">
		 🌌 ${t('memento.return_to_void_button', 'Return to the Void')}
	   </button>
	 </div>
   `;
 
   document.body.appendChild(completionPopup);
 
   // Safely inject dynamic text (prevents HTML injection)
   const eTxt = completionPopup.querySelector('#memento-eulogy-text');
   const epTxt = completionPopup.querySelector('#memento-epitaph-text');
   if (eTxt) eTxt.textContent = `"${llmEulogy}"`;
   if (epTxt) epTxt.textContent = `"${userEpitaph}"`;
 
   // Add click handler to log out user
   document.getElementById('return-to-void').addEventListener('click', async () => {
	 console.log('🌌 Return to Void button clicked');
	 completionPopup.remove();
 
	 try {
	   await logout();
	 } catch (error) {
	   console.error('❌ Error during logout:', error);
	   // Force redirect anyway
	   window.location.href = '/index.html';
	 }
   });
 }


// ========================================================================
// TIMER MANAGEMENT
// ========================================================================

/**
 * Start the Memento Mori countdown timer
 */
function startMementoMoriTimer(timeInMs) {
  console.log('⏰ Starting Memento Mori timer with', timeInMs, 'ms remaining');
  
  // Clear any existing timer
  if (mementoMoriTimer) {
	clearInterval(mementoMoriTimer);
  }
  
  let remainingTime = timeInMs;
  
  const updateAllTimers = () => {
	const minutes = Math.floor(remainingTime / 60000);
	const seconds = Math.floor((remainingTime % 60000) / 1000);
	const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;

	// Update announcement timer if it exists
	const announcementTimer = document.getElementById('memento-timer');
	if (announcementTimer) {
	  announcementTimer.textContent = timeString;
	}

	// Persistent timer removed - now handled by simulationTimer.js

	if (remainingTime <= 0) {
	  clearInterval(mementoMoriTimer);
	  handleMementoMoriTimeout();
	  return;
	}

	remainingTime -= 1000;

	// Update stored time for persistence
	if (mementoMoriData) {
	  const storageData = {
		...mementoMoriData,
		timeRemaining: remainingTime,
		storedAt: Date.now()
	  };
	  localStorage.setItem('activeMementoMori', JSON.stringify(storageData));
	}
  };
  
  // Update immediately
  updateAllTimers();
  
  // Start interval
  mementoMoriTimer = setInterval(updateAllTimers, 1000);
}

/**
 * Handle when the timer expires
 */
async function handleMementoMoriTimeout() {
  console.log('⏰ Memento Mori time has expired');
  
  // Clean up interfaces
  cleanupMementoMoriInterfaces();

  // Show loading popup while generating analysis
  const loadingPopup = document.createElement('div');
  loadingPopup.className = 'memento-modal';
  loadingPopup.style.cssText = `
	position: fixed; top: 0; left: 0; width: 100%; height: 100%;
	background: rgba(0, 0, 0, 0.95); z-index: 99999;
	display: flex; justify-content: center; align-items: center;
	font-family: 'Courier New', monospace;
  `;
  
  loadingPopup.innerHTML = `
	<div style="background: #000; border: 3px solid #ff6600; border-radius: 10px;
				padding: 40px; max-width: 500px; color: #ff6600; text-align: center;">
	  <h2 style="color: #ff6600; margin-bottom: 20px;">${t('memento.title','TIME EXPIRED')}</h2>
	  <div style="font-size: 3em; margin: 20px 0;"></div>
	  <p>${t('memento.body',"Death has come, but your portfolio's meaning is being preserved...")}</p>
	  <p style="font-size: 14px; margin-top: 15px;">${t('memento.generating','Generating your final eulogy...')}</p>
	</div>
  `;
  
  document.body.appendChild(loadingPopup);
  
  try {
	const objectiveId = mementoMoriData?.objective?._id;
	if (objectiveId) {
	  // Try to complete with auto-generated epitaph
	  const epitaph = 'Time expired before completion, but the attempt to understand my financial soul in death\'s shadow holds meaning beyond words.';
	  
	  const completionResponse = await fetch(`${apiBaseUrl}/api/memento-mori/complete`, {
		method: 'POST',
		headers: {
		  'Content-Type': 'application/json',
		  'Authorization': `Bearer ${getAuthToken()}`
		},
		body: JSON.stringify({
		  objectiveId: objectiveId,
		  finalEpitaph: epitaph
		})
	  });
	  
	  if (completionResponse.ok) {
		const result = await completionResponse.json();
		loadingPopup.remove();
		cleanupMementoMoriStorage();
		showDeathCompletion(result);
		return;
	  }
	}
  } catch (error) {
	console.error('❌ Error in timeout handling:', error);
  }
  
  // Fallback completion
  loadingPopup.remove();
  
  const fallbackResult = {
	personalInsight: "Time claimed you before completion, but your willingness to confront mortality through your portfolio reveals profound courage. In the urgency of finite time, you touched something eternal about the human search for meaning.",
	finalEpitaph: "A trader who dared to face death's questions, even when time itself became the final adversary."
  };
  
  cleanupMementoMoriStorage();
  showDeathCompletion(fallbackResult);
}

// ========================================================================
// UTILITY FUNCTIONS
// ========================================================================

/**
 * Get current time remaining
 */
function getTimeRemaining() {
  if (!mementoMoriData) return 0;
  
  const now = Date.now();
  const elapsed = now - (mementoMoriData.storedAt || now);
  return Math.max(0, mementoMoriData.timeRemaining - elapsed);
}

/**
 * Check reflection progress and show appropriate interface
 */
async function checkReflectionProgress() {
  try {
	const objectiveId = mementoMoriData?.objective?._id;
	if (!objectiveId) return;
	
	const response = await fetch(`${apiBaseUrl}/api/memento-mori/objective/${objectiveId}`, {
	  headers: {
		'Authorization': `Bearer ${getAuthToken()}`
	  }
	});
	
	if (response.ok) {
	  const data = await response.json();
	  const objective = data.objective;
	  
	  // Check status and show appropriate interface
	  if (objective.status === 'completed') {
		// Already completed, just show timer
		return;
	  } 
	}
  } catch (error) {
	console.error('Error checking reflection progress:', error);
  }
}

/**
 * Clean up all Memento Mori interfaces
 */
function cleanupMementoMoriInterfaces() {
  // Persistent timer removed - now handled by simulationTimer.js
  // const timerOverlay = document.getElementById('persistent-memento-timer');
  // if (timerOverlay) timerOverlay.remove();

  const oracleInterface = document.getElementById('mortality-oracle-interface');
  if (oracleInterface) oracleInterface.remove();

  const reflectionInterface = document.getElementById('reflection-interface');
  if (reflectionInterface) reflectionInterface.remove();

  const allocationInterface = document.getElementById('final-allocation-interface');
  if (allocationInterface) allocationInterface.remove();

  // Remove eulogy popup
  const eulogyPopup = document.getElementById('memento-mori-eulogy');
  if (eulogyPopup) eulogyPopup.remove();

  // Remove expanded eulogy modal
  const expandedEulogy = document.getElementById('expanded-eulogy-modal');
  if (expandedEulogy) expandedEulogy.remove();

  // Clear timer
  if (mementoMoriTimer) {
	clearInterval(mementoMoriTimer);
	mementoMoriTimer = null;
  }
   if (finalCurationTimeout) {
     clearTimeout(finalCurationTimeout);
     finalCurationTimeout = null;
   }

  // Clear eulogy update interval
  if (window.mementoEulogyInterval) {
	clearInterval(window.mementoEulogyInterval);
	window.mementoEulogyInterval = null;
  }

  // Reset initialization flag
  window.mementoMoriInitialized = false;
}

/**
 * Clean up Memento Mori storage
 */
function cleanupMementoMoriStorage() {
  localStorage.removeItem('activeMementoMori');
  localStorage.removeItem('mementoMoriStarted');
  localStorage.removeItem('simulationType');
}

/**
 * Animate oracle response with typewriter effect
 */
async function animateOracleResponse(data) {
  const textDiv = document.getElementById('mortality-oracle-text');
  const followUpDiv = document.getElementById('oracle-follow-up');
  
  // Clear previous content
  textDiv.innerHTML = '';
  followUpDiv.innerHTML = '';
  
  // Type out the main response
  const mainResponse = `"${data.oracleResponse}"`;
  await typeWriterEffect(textDiv, mainResponse, 30);
  
  // Add follow-up insight if available
  if (data.followUpInsight) {
	setTimeout(async () => {
	  followUpDiv.innerHTML = `
		<div style="color: rgba(255, 135, 0, 0.9); font-weight: bold; margin-bottom: 8px;">
		  ${t('memento.further_insight',"The Market's Further Insight:")}
		</div>
		<div style="color: #00ff00; font-size: 0.9em;"></div>
	  `;
	  
	  const insightText = followUpDiv.querySelector('div:last-child');
	  await typeWriterEffect(insightText, data.followUpInsight, 25);
	  
	  // Add personalized guidance if available
	  if (data.personalizedGuidance) {
		setTimeout(async () => {
		  const guidanceDiv = document.createElement('div');
		  guidanceDiv.style.cssText = 'margin-top: 10px; color: rgba(0, 255, 0, 0.8); font-size: 0.85em; font-style: italic;';
		  followUpDiv.appendChild(guidanceDiv);
		  await typeWriterEffect(guidanceDiv, `→ ${data.personalizedGuidance}`, 20);
		}, 1000);
	  }
	}, 1500);
  }
}

/**
 * Typewriter effect for text animation
 */
async function typeWriterEffect(element, text, speed = 30) {
  return new Promise((resolve) => {
	let i = 0;
	const timer = setInterval(() => {
	  if (i < text.length) {
		element.textContent += text.charAt(i);
		i++;
	  } else {
		clearInterval(timer);
		resolve();
	  }
	}, speed);
  });
}

/**
 * Logout function
 */
async function logout() {
  const portfolioId = localStorage.getItem('portfolioId');
  const token = getAuthToken();
  const isSimulated = localStorage.getItem('isSimulated') === 'true';

  if (!portfolioId || !token) {
	console.error('Portfolio ID or token not found in local storage');
	return;
  }

  try {
	// First, disconnect all bot sockets if this is a simulated user
	if (isSimulated && socket && socket.connected) {
	  console.log('🤖 Disconnecting bots for simulated user logout...');
	  socket.emit('disconnectUserBots', { portfolioId });
	  
	  // Wait a moment for bot disconnection to complete
	  await new Promise(resolve => setTimeout(resolve, 1000));
	}

	// Update the active status to false on the server
	const response = await fetch(`${apiBaseUrl}/api/portfolios/${portfolioId}`, {
	  method: 'PATCH',
	  headers: {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${token}`,
	  },
	  body: JSON.stringify({ active: false }),
	});

	if (!response.ok) {
	  throw new Error('Failed to update active status during logout');
	}

  } catch (error) {
	console.error('Error updating active status during logout:', error);
  }

  try {
	// Call the logout endpoint on the server
	const logoutResponse = await fetch(`${apiBaseUrl}/api/logout`, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${token}`,
	  },
	  body: JSON.stringify({ portfolioId, isSimulated }),
	  credentials: 'include'
	});

	if (!logoutResponse.ok) {
	  throw new Error('Failed to logout from server');
	}

	const logoutData = await logoutResponse.json();
	console.log(logoutData.message);
	
  } catch (error) {
	console.error('Error during server logout:', error);
  }

  // Disconnect main socket
  if (socket && socket.connected) {
	console.log('🔌 Disconnecting main socket...');
	socket.disconnect();
  }

  // Clear all storage
  localStorage.clear();
  
  // Clear cookies
  document.cookie.split(";").forEach((cookie) => {
	const name = cookie.split("=")[0].trim();
	document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax;Secure`;
  });

  window.location.href = '/index.html';
}


export async function checkAndShowMementoMori(portfolioId) {
  // Check local storage first
  const storedObjective = localStorage.getItem('activeMementoMori');
  const simulationType = localStorage.getItem('simulationType');
  
  if (storedObjective && simulationType === 'memento-mori') {
	console.log('✅ Memento Mori found in local storage. Restoring...');
	checkAndRestoreMementoMori();
	return;
  }
  
  try {
	const res = await fetch(`${apiBaseUrl}/api/memento-mori/portfolio/${portfolioId}/status`, {
	  headers: {
		'Authorization': `Bearer ${getAuthToken()}`
	  }
	});
	
	if (!res.ok) {
	  console.error('Failed to fetch Memento Mori status');
	  return;
	}
	
	const data = await res.json();
	
	if (data.success && data.activeObjective) {
	  console.log('⚰️ New Memento Mori objective found:', data.activeObjective);
	  
	  showMementoMoriAnnouncement({
		objective: data.activeObjective,
		timeRemaining: data.activeObjective.timeRemaining
	  });
	}
  } catch (err) {
	console.error('Error checking Memento Mori status:', err);
  }
}

// =====================================================
// NEW 3-PHASE MEMENTO MORI FLOW FUNCTIONS
// =====================================================

/**
 * Show new reflection interface for Phase 1
 */



/**
 * Show value analysis results and transition to trading phase
 */
function showValueAnalysisResults(analysisResult) {
  console.log('🎯 Starting Phase 2: Values-Based Trading');
  
  mementoMoriPhase = 'trading';

  const resultsInterface = document.createElement('div');
  resultsInterface.id = 'value-analysis-results';
  resultsInterface.className = 'memento-mori-interface';
  resultsInterface.style.cssText = `
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: linear-gradient(135deg, #1a1a1a 0%, #1b2d1b 100%);
	border: 2px solid #228B22;
	border-radius: 15px;
	padding: 30px;
	z-index: 10000;
	max-width: 700px;
	max-height: 80vh;
	overflow-y: auto;
	box-shadow: 0 0 50px rgba(34, 139, 34, 0.8);
	color: white;
	font-family: 'Courier New', monospace;
  `;

  const recommendations = analysisResult.recommendedCompanies || [];
  const guidance = analysisResult.message || 'Trade based on your values, not profit.';

  resultsInterface.innerHTML = `
	<div style="text-align: center; margin-bottom: 25px;">
	  <h2 style="color: #90EE90; margin: 0; font-size: 24px;">${t('memento.title','YOUR VALUE PROFILE')}</h2>
	  <p style="color: #ccc; margin: 10px 0;">${t('memento.subtitle','Based on your reflections, here are companies that align with your values')}</p>
	</div>
	
	<div style="margin-bottom: 20px;">
	  <h3 style="color: #90EE90; margin-bottom: 10px;">${t('memento.guidance_title','Guidance:')}</h3>
	  <p style="color: #ddd; line-height: 1.5; background: #2a2a2a; padding: 15px; border-radius: 8px;">
		${guidance}
	  </p>
	</div>
	
	<div style="margin-bottom: 25px;">
	  <h3 style="color: #90EE90; margin-bottom: 10px;">${t('memento.recommended_title','Recommended Companies:')}</h3>
	  <div style="background: #2a2a2a; padding: 15px; border-radius: 8px; max-height: 200px; overflow-y: auto;">
		${recommendations.map(company => {
		  const companyName = typeof company === 'object' ? company.companyName || company.name || 'Unknown Company' : company;
		  const reason = typeof company === 'object' ? company.reason || '' : '';
		  return `<div style="color: #fff; margin: 8px 0; padding: 5px;">
			<strong>• ${companyName}</strong>
			${reason ? `<br><span style="color: #aaa; font-size: 0.9em; margin-left: 12px;">${reason}</span>` : ''}
		  </div>`;
		}).join('')}
	  </div>
	</div>
	
	<div style="text-align: center; margin-top: 25px;">
	  <button id="start-trading" style="
		background: linear-gradient(45deg, #228B22, #32CD32);
		color: white;
		border: none;
		padding: 12px 25px;
		border-radius: 25px;
		cursor: pointer;
		font-size: 16px;
		font-weight: bold;
		transition: all 0.3s;
	  ">
		${t('memento.start_trading','Start Trading (All 310+ Companies Available) →')}
	  </button>
	</div>
  `;

  document.body.appendChild(resultsInterface);
  document.getElementById('start-trading').addEventListener('click', startTradingPhase);
}

/**
 * Start trading phase with all companies available
 */
function startTradingPhase() {
   console.log('💰 Phase 2: Trading with all companies available');

   const resultsInterface = document.getElementById('value-analysis-results');
   if (resultsInterface) resultsInterface.remove();
 
   localStorage.setItem('simulationType', 'memento-mori');    
   localStorage.setItem('mementoShowAllCompanies', 'true');   
ing
   try {
	 window.dispatchEvent(new CustomEvent('memento:showAllCompanies'));
   } catch {}

   try {
	 if (typeof displayPortfolioItems === 'function') {
	   displayPortfolioItems(true);     
	 } else if (window.displayPortfolioItems) {
	   window.displayPortfolioItems(true);
	 } else {
	   setTimeout(() => window.location.reload(), 500);
	 }
   } catch (err) {
	 console.error('Error refreshing market:', err);
	 setTimeout(() => window.location.reload(), 500);
   }

   const tradingNotification = document.createElement('div');
   tradingNotification.style.cssText = `
	 position: fixed;
	 top: 20px;
	 right: 20px;
	 background: linear-gradient(45deg, #228B22, #32CD32);
	 color: white;
	 padding: 15px 20px;
	 border-radius: 10px;
	 z-index: 9999;
	 box-shadow: 0 4px 20px rgba(34, 139, 34, 0.5);
	 font-family: 'Courier New', monospace;
	 border: 2px solid #90EE90;
   `;
   tradingNotification.innerHTML = `
	 <div style="font-weight: bold; margin-bottom: 5px;">${t('memento.active_badge','MEMENTO MORI TRADING ACTIVE')}</div>
	 <div style="font-size: 14px;">${t('memento.all_available','All 310+ companies available for trading')}</div>
	 <div style="font-size: 12px; margin-top: 5px; opacity: 0.8;">${t('memento.values_over_profit','Trade based on your values, not profit')}</div>
   `;
   document.body.appendChild(tradingNotification);

   setTimeout(() => {
	 if (mementoMoriPhase === 'trading') {
	   showFinalCompletionReminder();
	 }
   }, (mementoMoriData?.timeRemaining || 900000) - 180000);
 }


/**
 * Show reminder to complete the simulation
 */
function showFinalCompletionReminder() {
  const reminder = document.createElement('div');
  reminder.style.cssText = `
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: linear-gradient(135deg, #8B0000, #DC143C);
	color: white;
	padding: 20px;
	border-radius: 10px;
	z-index: 10000;
	text-align: center;
	font-family: 'Courier New', monospace;
	border: 2px solid #ff4444;
  `;
  
  reminder.innerHTML = `
	<h3 style="margin: 0 0 10px 0;">${t('memento.title','FINAL CALL')}</h3>
	<p style="margin: 0 0 15px 0;">${t('memento.body','Time is running out. Complete your Memento Mori simulation now.')}</p>
	<button id="final-complete" style="
	  background: #8B0000;
	  color: white;
	  border: none;
	  padding: 10px 20px;
	  border-radius: 5px;
	  cursor: pointer;
	">
	  ${t('memento.complete_now','Complete Now →')}
	</button>
  `;

  document.body.appendChild(reminder);
  document.getElementById('final-complete').addEventListener('click', () => {
	reminder.remove();
	completeNewMementoMoriFlow();
  });

  setTimeout(() => {
	if (reminder.parentNode) {
	  reminder.remove();
	}
  }, 30000);
}

/**
 * Complete the new Memento Mori flow with portfolio analysis
 */
async function completeNewMementoMoriFlow() {
  console.log('🪦 Phase 3: Completing Memento Mori with portfolio analysis');
  
  mementoMoriPhase = 'completed';

  // Clear any existing timers
  clearTimeout(forcedDeathTimeout);

  try {
	const objectiveId = mementoMoriData?.objective?._id;
	const response = await fetch(`${apiBaseUrl}/api/memento-mori/complete`, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${getAuthToken()}`
	  },
	  body: JSON.stringify({
		objectiveId: objectiveId,
		finalReflections: {} // Could add final reflection questions here
	  })
	});

	if (!response.ok) {
	  throw new Error('Failed to complete Memento Mori');
	}

	const result = await response.json();
	console.log('✅ Memento Mori completed:', result);

	// Show final eulogy
	showFinalEulogy(result);

  } catch (error) {
	console.error('Error completing Memento Mori:', error);
	alert('Failed to complete Memento Mori simulation.');
  }
}

/**
 * Show the final portfolio-based eulogy
 */
function showFinalEulogy(completionResult) {
  const eulogyInterface = document.createElement('div');
  eulogyInterface.style.cssText = `
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: linear-gradient(135deg, #1a1a1a 0%, #2d1b1b 100%);
	border: 2px solid #8B0000;
	border-radius: 15px;
	padding: 30px;
	z-index: 10000;
	max-width: 800px;
	max-height: 80vh;
	overflow-y: auto;
	box-shadow: 0 0 50px rgba(139, 0, 0, 0.8);
	color: white;
	font-family: 'Courier New', monospace;
  `;

  const eulogy = completionResult.eulogy || 'Your portfolio reflected deep consideration of life values.';
  const insights = completionResult.portfolioInsights || [];

  eulogyInterface.innerHTML = `
	<div style="text-align: center; margin-bottom: 25px;">
	  <h2 style="color: #ff4444; margin: 0; font-size: 24px;">${t('memento.title','YOUR DIGITAL EULOGY')}</h2>
	  <p style="color: #ccc; margin: 10px 0;">${t('memento.subtitle','Based on your portfolio choices and life reflections')}</p>
	</div>
	
	<div style="background: #2a2a2a; padding: 20px; border-radius: 10px; margin-bottom: 20px;">
	  <h3 style="color: #ff6b6b; margin-bottom: 15px;">${t('memento.your_eulogy','Your Eulogy:')}</h3>
	  <div style="color: #ddd; line-height: 1.6; font-style: italic;">
		"${eulogy}"
	  </div>
	</div>
	
	${insights.length > 0 ? `
	<div style="background: #2a2a2a; padding: 20px; border-radius: 10px; margin-bottom: 20px;">
	  <h3 style="color: #ff6b6b; margin-bottom: 15px;">${t('memento.insights','Portfolio Insights:')}</h3>
	  ${insights.map(insight => `<div style="color: #ddd; margin: 8px 0;">• ${insight}</div>`).join('')}
	</div>
	` : ''}
	
	<div style="text-align: center; margin-top: 25px;">
	  <button id="close-eulogy" style="
		background: linear-gradient(45deg, #8B0000, #DC143C);
		color: white;
		border: none;
		padding: 12px 25px;
		border-radius: 25px;
		cursor: pointer;
		font-size: 16px;
		font-weight: bold;
	  ">
		${t('memento.rest_in_peace','Rest in Peace')}
	  </button>
	</div>
  `;

  document.body.appendChild(eulogyInterface);
  document.getElementById('close-eulogy').addEventListener('click', () => {
	eulogyInterface.remove();
	localStorage.removeItem('mementoMoriStarted');
	
	// Remove any trading notifications
	const notifications = document.querySelectorAll('[style*="MEMENTO MORI TRADING ACTIVE"]');
	notifications.forEach(notification => notification.remove());
  });
}
function cancelMementoMoriPhaseTimers() {

}

// ✅ Listen for timer expiry event from simulationTimer.js
window.addEventListener('mementoMoriExpired', async () => {
  console.log('⚰️ [objectives.js] Memento Mori timer expired - completing session and redirecting');

  try {
	const objectiveId = localStorage.getItem('mementoMoriObjectiveId');
	if (!objectiveId) {
	  console.error('❌ No objective ID found for completion');
	  // Redirect anyway with fallback
	  redirectToMementoMoriEnd({
		eulogy: "Your journey through life's marketplace has concluded.",
		portfolioInsights: []
	  });
	  return;
	}

	// Call the completion endpoint
	const response = await fetch(`${apiBaseUrl}/api/memento-mori/complete`, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${getAuthToken()}`
	  },
	  body: JSON.stringify({
		objectiveId: objectiveId,
		finalReflections: {}
	  })
	});

	if (!response.ok) {
	  throw new Error('Failed to complete Memento Mori');
	}

	const result = await response.json();
	console.log('✅ Memento Mori auto-completed on timer expiry:', result);

	// Redirect to memento mori ending page with eulogy data
	redirectToMementoMoriEnd(result);

  } catch (error) {
	console.error('❌ Error auto-completing Memento Mori on timer expiry:', error);
	// Redirect with fallback eulogy
	redirectToMementoMoriEnd({
	  eulogy: "Your journey through life's marketplace has concluded. In facing mortality, you discovered what truly matters.",
	  portfolioInsights: []
	});
  }
});

// Redirect to memento mori ending page with eulogy data
function redirectToMementoMoriEnd(eulogyData) {
  console.log('🪦 Redirecting to Memento Mori ending page...');

  try {
	// Store data in temporary localStorage (survives the logout/redirect)
	localStorage.setItem('tempMementoMoriResults', JSON.stringify({
	  ...eulogyData,
	  timestamp: Date.now()
	}));

	console.log('💾 Stored eulogy data for ending page');

	// Redirect to the ending page
	setTimeout(() => {
	  window.location.href = '/mementomoriend.html';
	}, 100);

  } catch (error) {
	console.error('❌ Error preparing redirect:', error);
	// Try redirect anyway
	window.location.href = '/mementomoriend.html';
  }
}

export function cleanupMementoMoriTimer() {
  if (mementoMoriTimer) {
	clearInterval(mementoMoriTimer);
	mementoMoriTimer = null;
  }
  cancelMementoMoriPhaseTimers(); // Also clean phase timers
  cleanupMementoMoriInterfaces();
  cleanupMementoMoriStorage();
}